// Safe wrappers around Supabase auth calls to avoid "infinite loading" UI states.
// These DO NOT change auth behavior; they only add timeouts and local fallback paths.

export const withTimeout = async (promise, ms, label) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`${label} timed out after ${ms}ms`);
      error.code = 'TIMEOUT';
      reject(error);
    }, ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const safeSignOutLocal = async (supabase, { timeoutMs = 1500 } = {}) => {
  // Local sign-out clears browser storage without requiring network.
  // This is the best "unstick me" option when auth endpoints hang.
  return await withTimeout(
    supabase.auth.signOut({ scope: 'local' }),
    timeoutMs,
    'supabase.auth.signOut(local)'
  );
};

export const safeSignOutGlobal = async (supabase, { timeoutMs = 3500 } = {}) => {
  // Global sign-out revokes refresh tokens server-side (best effort).
  return await withTimeout(
    supabase.auth.signOut({ scope: 'global' }),
    timeoutMs,
    'supabase.auth.signOut(global)'
  );
};

export const hardResetAuth = async (
  supabase,
  { revokeRemote = false, remoteTimeoutMs = 3500 } = {}
) => {
  // 1) Optional best-effort remote revocation.
  if (revokeRemote) {
    try {
      await safeSignOutGlobal(supabase, { timeoutMs: remoteTimeoutMs });
    } catch {
      // ignore
    }
  }

  // 2) Best-effort local sign out (clears most keys).
  try {
    await safeSignOutLocal(supabase, { timeoutMs: 1500 });
  } catch {
    // ignore
  }

  // 3) Deterministic cleanup of any leftover keys (covers edge cases).
  try {
    const { clearSupabaseAuthStorage } = await import('./supabaseAuthStorage');
    clearSupabaseAuthStorage();
  } catch {
    // ignore
  }
};

export const safeGetUser = async (supabase, { timeoutMs = 7000 } = {}) => {
  return await withTimeout(supabase.auth.getUser(), timeoutMs, 'supabase.auth.getUser');
};

export const safeGetSession = async (supabase, { timeoutMs = 3000 } = {}) => {
  return await withTimeout(supabase.auth.getSession(), timeoutMs, 'supabase.auth.getSession');
};

export const safeSignInWithPassword = async (
  supabase,
  { email, password, timeoutMs = 10000 } = {}
) => {
  return await withTimeout(
    supabase.auth.signInWithPassword({ email, password }),
    timeoutMs,
    'supabase.auth.signInWithPassword'
  );
};

export const safeSignUpWithPassword = async (
  supabase,
  { email, password, emailRedirectTo, data, timeoutMs = 10000 } = {}
) => {
  const options = {};
  if (emailRedirectTo) options.emailRedirectTo = emailRedirectTo;
  // data is stored in auth.users.raw_user_meta_data (e.g. { referral_code })
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    options.data = data;
  }
  return await withTimeout(
    supabase.auth.signUp({
      email,
      password,
      options: Object.keys(options).length > 0 ? options : undefined,
    }),
    timeoutMs,
    'supabase.auth.signUp'
  );
};

export const safeRefreshSession = async (supabase, { timeoutMs = 7000 } = {}) => {
  return await withTimeout(
    supabase.auth.refreshSession(),
    timeoutMs,
    'supabase.auth.refreshSession'
  );
};

export const safeResetPasswordForEmail = async (
  supabase,
  { email, redirectTo, timeoutMs = 10000 } = {}
) => {
  return await withTimeout(
    supabase.auth.resetPasswordForEmail(email, redirectTo ? { redirectTo } : undefined),
    timeoutMs,
    'supabase.auth.resetPasswordForEmail'
  );
};

export const safeResendSignupEmail = async (
  supabase,
  { email, emailRedirectTo, timeoutMs = 10000 } = {}
) => {
  return await withTimeout(
    supabase.auth.resend({
      type: 'signup',
      email,
      options: emailRedirectTo ? { emailRedirectTo } : undefined,
    }),
    timeoutMs,
    'supabase.auth.resend(signup)'
  );
};

export const safeExchangeCodeForSession = async (
  supabase,
  { code, timeoutMs = 10000 } = {}
) => {
  return await withTimeout(
    supabase.auth.exchangeCodeForSession(code),
    timeoutMs,
    'supabase.auth.exchangeCodeForSession'
  );
};

export const safeUpdateUserPassword = async (
  supabase,
  { password, timeoutMs = 10000 } = {}
) => {
  return await withTimeout(
    supabase.auth.updateUser({ password }),
    timeoutMs,
    'supabase.auth.updateUser(password)'
  );
};

